<script>
	import { dayjs } from '@woltair/translations-package-fe'
	import { page } from '$app/stores'
	import ConditionalAnchor from '$lib/components/ConditionalAnchor.svelte'
	import { HEAT_SOURCE_ANSWER, HEAT_SOURCE_TYPE_NAME } from '$lib/consts'
	import { WOLTADVISOR, HEAT_PUMP } from '$lib/routes'
	import { t } from '$lib/translations'
	import { Anchor } from '$lib/ui-woltair/components'

	export let references = []
	export let cityName = null
	export let regionName = null
	export let deviceType = null
	export let title = 'Reference montáží'
	export let cls = ''
	export let brands = {}

	references.forEach((ref) => {
		ref.order?.orderEvaluation?.find((feedback) => {
			if (
				typeof feedback.satisfactionWithExpertDescription === 'string' &&
				feedback.satisfactionWithExpert >= 5
			) {
				ref.feedback = feedback
			}
		})
	})

	for (const reference of references) {
		reference.resources.displayed = 0
	}

	function getFirstName(fullName) {
		const prefixes = ['Ing', 'Mgr', 'Bc', 'DiS']
		return fullName.split(' ')[prefixes.includes(fullName.split(' ')[0].replace('.', '')) ? 1 : 0]
	}

	function getBrandLink(brandName) {
		if (!brands) return null

		const matchingBrand = brands?.find(
			(brand) => slugify(brand.attributes.title) === slugify(brandName)
		)

		if (
			matchingBrand &&
			matchingBrand.attributes.slug &&
			matchingBrand.attributes?.brandPages?.data?.some(
				(brandPage) =>
					brandPage.attributes?.locale === $page.data.organization.lang.toLocaleLowerCase()
			)
		)
			return `/${HEAT_PUMP}/${matchingBrand.attributes.slug}`

		return null
	}

	function slugify(str) {
		return String(str)
			.normalize('NFKD')
			.replace(/[\u0300-\u036f]/g, '')
			.trim()
			.toLowerCase()
			.replace(/[^a-z0-9 -]/g, '')
			.replace(/\s+/g, '-')
			.replace(/-+/g, '-')
	}
</script>

{#if references.length > 1}
	<section class="{cls} flex flex-col">
		<h2>{title} {cityName || regionName || ''}</h2>
		<div class="mt-16 flex flex-col gap-12 md:gap-24">
			{#each references as reference}
				<article class="item container-flex flex max-w-4xl flex-col gap-x-16 gap-y-8 md:flex-row">
					<div class="gallery-cont">
						<Anchor padded id="ref{reference.order.uid}" />
						<div class="displayed-image-cont">
							<img
								class="rounded-xl"
								src={reference.resources[reference.resources.displayed || 0].url}
								alt="Produkt-{reference.order.uid}"
							/>
						</div>
						<div class="thumbnails">
							{#each reference.resources as resource, i}
								{#if reference.resources.displayed !== i}
									<div
										class="thumbnail"
										role="presentation"
										on:click={() => {
											reference.resources.displayed = i
										}}
									>
										<img src={resource.url} alt="Reference" />
									</div>
								{/if}
							{/each}
						</div>
						<div class="customer mt-1">
							<div class="font-semibold">{getFirstName(reference.order?.customer?.name)}</div>
							<div class="opacity-60">
								{reference.order.location?.address?.city}, {dayjs(reference.createdAt).format(
									'MMMM YYYY'
								)}
							</div>
						</div>
					</div>
					<div class="description-cont flex w-full flex-col gap-6">
						{#if reference.feedback?.satisfactionWithExpertDescription}
							<div class="rounded-xl bg-neutral-100 px-6 py-4">
								<h5 class="font-semibold text-w-blue-950">Hodnocení techniků Woltair</h5>
								<div class="mt-2 italic opacity-70">
									{reference.feedback.satisfactionWithExpertDescription}
								</div>
							</div>
						{/if}
						<table class="overflow-hidden rounded-xl">
							{#if reference.order.installationOrder?.supplier?.name}
								{@const brandPageLink = getBrandLink(
									reference.order.installationOrder?.supplier?.name
								)}
								<tr>
									<td class="subtext text-gray">{HEAT_SOURCE_TYPE_NAME(deviceType)}:</td>
									<td class="subtext text-gray font-semibold text-w-blue-950">
										<ConditionalAnchor
											href={brandPageLink}
											condition={!!brandPageLink === true}
											cls="hover:underline"
										>
											{reference.order.installationOrder?.supplier?.name}</ConditionalAnchor
										></td
									>
								</tr>
							{/if}
							{#if reference.order.installationOrder.existingHeatSource}
								<tr>
									<td class="subtext text-gray">Přechozí zdroj vytápění:</td>
									<td class="subtext text-gray">
										{HEAT_SOURCE_ANSWER(reference.order.installationOrder.existingHeatSource)}
									</td>
								</tr>
							{/if}
							{#if reference.order.installationOrder.heatedArea}
								<tr>
									<td class="subtext text-gray">Plocha domu:</td>
									<td class="subtext text-gray"
										>{reference.order.installationOrder.heatedArea} m&#178;</td
									>
								</tr>
							{/if}
							{#if typeof reference.order.installationOrder.houseHeatLoss === 'number'}
								<tr>
									<td class="subtext text-gray">Tepelná ztráta:</td>
									<td class="subtext text-gray"
										>{reference.order.installationOrder.houseHeatLoss} kW</td
									>
								</tr>
							{/if}
							{#if typeof reference.order.installationOrder.houseEstimatedHeatLoss === 'number'}
								<tr>
									<td class="subtext text-gray">Odhad tepelné ztráty:</td>
									<td class="subtext text-gray"
										>{reference.order.installationOrder.houseEstimatedHeatLoss} kW</td
									>
								</tr>
							{/if}
							{#if reference.order.installationOrder.houseBuiltOrApproved}
								<tr>
									<td class="subtext text-gray">Stavba postavena:</td>
									<td class="subtext text-gray">
										{#if t('web.references.houseBuiltApproved.' + reference.order.installationOrder.houseBuiltOrApproved) != 'web.references.houseBuiltApproved.' + reference.order.installationOrder.houseBuiltOrApproved}
											{t(
												'web.references.houseBuiltApproved.' +
													reference.order.installationOrder.houseBuiltOrApproved
											)}{:else}{reference.order.installationOrder.houseBuiltOrApproved}{/if}</td
									>
								</tr>
							{/if}
							{#if typeof reference.order.installationOrder.residentsAmount === 'number'}
								<tr>
									<td class="subtext text-gray">Počet rezidentů:</td>
									<td class="subtext text-gray"
										>{reference.order.installationOrder.residentsAmount}</td
									>
								</tr>
							{/if}
						</table>
						<a class="btn-new btn-new-red" href="/{deviceType}/{WOLTADVISOR}"
							>Získat cenu vč. montáže</a
						>
					</div>
				</article>
			{/each}
		</div>
	</section>
{/if}

<style lang="scss">
	article {
		@media (max-width: 760px) {
			flex-direction: column;
			margin: 50px 0;
		}

		.gallery-cont {
			width: 500px;

			@media (max-width: 760px) {
				width: 100%;
				margin-right: 0;
			}

			.displayed-image-cont {
				width: 100%;
				height: 350px;

				@media (max-width: 760px) {
					height: auto;
				}

				img {
					width: 100%;
					height: 100%;
					object-fit: cover;
					display: block;
				}
			}

			.thumbnails {
				margin-top: 10px;
				overflow-x: auto;
				display: flex;
				-ms-overflow-style: none;
				scrollbar-width: none;

				&::-webkit-scrollbar {
					display: none;
				}

				.thumbnail {
					margin-right: 10px;
					flex-shrink: 0;
					width: 200px;
					height: 120px;
					cursor: pointer;

					&:last-child {
						margin-right: 0;
					}

					img {
						width: 100%;
						height: 100%;
						object-fit: cover;
						display: block;
					}
				}
			}
		}

		.description-cont {
			@media (max-width: 760px) {
				width: 100%;
			}

			table {
				border-collapse: collapse;
				width: 100%;

				tr {
					&:not(:last-of-type) {
						border-bottom: 1px solid #ccc;
					}
					td {
						width: 50%;
						@apply px-6 py-3;
					}

					td:last-of-type,
					td:first-of-type {
						border-radius: 0 !important;
					}
				}
			}
		}
	}
</style>
